import { useEffect, useMemo, useState } from "react";
import {
  ActionIcon,
  Anchor,
  Badge,
  Button,
  Container,
  Divider,
  Drawer,
  Group,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  UnstyledButton,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconArrowDown, IconArrowUp, IconArrowsSort, IconEdit, IconPlus } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { ApiError, api } from "../api/client";
import type { Customer, HoldingRow } from "../api/types";
import { fmtQty } from "../components/freeIssues/qty";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";

const DRIVE_FOLDER_PREFIX = "https://drive.google.com/drive/folders/";

type CustomerSortKey = "name" | "contact_email" | "contact_phone" | "drive_folder_id" | "is_active";
type SortDir = "asc" | "desc";

export function Customers() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const canEdit = user.role === "admin" || user.role === "operator";

  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Customer | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sortKey, setSortKey] = useState<CustomerSortKey>("name");
  const [sortDir, setSortDir] = useState<SortDir>("asc");

  async function load() {
    setLoading(true);
    try {
      setCustomers(await api.listCustomers());
    } catch {
      notifications.show({ color: "red", title: t("common.error"), message: t("customers.loadFailed") });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleSort(key: CustomerSortKey) {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir(key === "is_active" ? "desc" : "asc");
    }
  }

  const displayedRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const sortable = [...customers];
    sortable.sort((a, b) => {
      let av: string | number = "";
      let bv: string | number = "";
      switch (sortKey) {
        case "name": av = a.name; bv = b.name; break;
        case "contact_email": av = a.contact_email ?? ""; bv = b.contact_email ?? ""; break;
        case "contact_phone": av = a.contact_phone ?? ""; bv = b.contact_phone ?? ""; break;
        case "drive_folder_id": av = a.drive_folder_id ?? ""; bv = b.drive_folder_id ?? ""; break;
        case "is_active": av = a.is_active ? 1 : 0; bv = b.is_active ? 1 : 0; break;
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
    return sortable;
  }, [customers, sortKey, sortDir]);

  function SortHeader({ k, label }: { k: CustomerSortKey; label: string }) {
    const isActive = sortKey === k;
    const Icon = isActive ? (sortDir === "asc" ? IconArrowUp : IconArrowDown) : IconArrowsSort;
    return (
      <UnstyledButton
        onClick={() => toggleSort(k)}
        style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", fontWeight: 600 }}
      >
        {label}
        <Icon size={12} style={{ opacity: isActive ? 1 : 0.4 }} />
      </UnstyledButton>
    );
  }

  function openCreate() {
    setEditing(null);
    setDrawerOpen(true);
  }

  function openEdit(c: Customer) {
    setEditing(c);
    setDrawerOpen(true);
  }

  return (
    <Container size="xl">
      <Stack mb="md" gap="sm">
        <Title order={2}>{t("customers.title")}</Title>
        {canEdit && (
          <Group gap="xs" wrap="wrap">
            <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>
              {t("customers.add")}
            </Button>
          </Group>
        )}
      </Stack>

      <ResponsiveTable
        mobile={{
          rows: displayedRows,
          sort: {
            options: [
              { value: "name", label: t("customers.colName") },
              { value: "contact_email", label: t("customers.colEmail") },
              { value: "contact_phone", label: t("customers.colPhone") },
              { value: "drive_folder_id", label: t("customers.colDriveFolder") },
              { value: "is_active", label: t("customers.colStatus") },
            ],
            value: sortKey,
            dir: sortDir,
            onChange: (v, d) => (v === sortKey ? setSortDir(d) : toggleSort(v as CustomerSortKey)),
          },
          rowKey: (c) => c.id,
          title: (c) => <Text span size="sm" fw={600}>{c.name}</Text>,
          subtitle: (c) => c.address ?? undefined,
          badge: (c) => (
            <Badge color={c.is_active ? "green" : "gray"} variant="light">
              {c.is_active ? t("common.active") : t("common.inactive")}
            </Badge>
          ),
          fields: [
            { label: t("customers.colEmail"), render: (c) => c.contact_email ?? t("common.none") },
            { label: t("customers.colPhone"), render: (c) => c.contact_phone ?? t("common.none") },
            {
              label: t("customers.colDriveFolder"),
              render: (c) => c.drive_folder_id ? (
                <Anchor href={`${DRIVE_FOLDER_PREFIX}${c.drive_folder_id}`} target="_blank" size="xs">
                  {c.drive_folder_id.slice(0, 14)}…
                </Anchor>
              ) : (
                <Text c="dimmed" size="xs">{t("common.unset")}</Text>
              ),
            },
          ],
          actions: canEdit ? (c) => (
            <ActionIcon variant="subtle" size="lg" onClick={() => openEdit(c)}>
              <IconEdit size={18} />
            </ActionIcon>
          ) : undefined,
          loading: loading && customers.length === 0,
          empty: <>{t("customers.empty")} {canEdit && t("customers.emptyHint")}</>,
        }}
      >
      <Table.ScrollContainer minWidth={720}>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th><SortHeader k="name" label={t("customers.colName")} /></Table.Th>
            <Table.Th><SortHeader k="contact_email" label={t("customers.colEmail")} /></Table.Th>
            <Table.Th><SortHeader k="contact_phone" label={t("customers.colPhone")} /></Table.Th>
            <Table.Th><SortHeader k="drive_folder_id" label={t("customers.colDriveFolder")} /></Table.Th>
            <Table.Th><SortHeader k="is_active" label={t("customers.colStatus")} /></Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {loading && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text c="dimmed">{t("common.loading")}</Text>
              </Table.Td>
            </Table.Tr>
          )}
          {!loading && customers.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={6}>
                <Text c="dimmed">
                  {t("customers.empty")} {canEdit && t("customers.emptyHint")}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
          {displayedRows.map((c) => (
            <Table.Tr key={c.id}>
              <Table.Td>
                <Text fw={500}>{c.name}</Text>
                {c.address && (
                  <Text size="xs" c="dimmed">
                    {c.address}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>{c.contact_email ?? t("common.none")}</Table.Td>
              <Table.Td>{c.contact_phone ?? t("common.none")}</Table.Td>
              <Table.Td>
                {c.drive_folder_id ? (
                  <Anchor
                    href={`${DRIVE_FOLDER_PREFIX}${c.drive_folder_id}`}
                    target="_blank"
                    size="xs"
                  >
                    {c.drive_folder_id.slice(0, 14)}…
                  </Anchor>
                ) : (
                  <Text c="dimmed" size="xs">
                    {t("common.unset")}
                  </Text>
                )}
              </Table.Td>
              <Table.Td>
                <Badge color={c.is_active ? "green" : "gray"} variant="light">
                  {c.is_active ? t("common.active") : t("common.inactive")}
                </Badge>
              </Table.Td>
              <Table.Td>
                {canEdit && (
                  <ActionIcon variant="subtle" onClick={() => openEdit(c)}>
                    <IconEdit size={16} />
                  </ActionIcon>
                )}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      </Table.ScrollContainer>
      </ResponsiveTable>

      <CustomerDrawer
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        customer={editing}
        onSaved={() => {
          setDrawerOpen(false);
          load();
        }}
      />
    </Container>
  );
}

interface DrawerProps {
  opened: boolean;
  onClose: () => void;
  customer: Customer | null;
  onSaved: () => void;
}

function CustomerDrawer({ opened, onClose, customer, onSaved }: DrawerProps) {
  const { t } = useTranslation();
  const isEdit = customer !== null;
  const [submitting, setSubmitting] = useState(false);
  const [materials, setMaterials] = useState<HoldingRow[]>([]);

  // „Materiale la client” (livrări libere) — doar citire; ascuns când e gol.
  useEffect(() => {
    setMaterials([]);
    if (!opened || !customer) return;
    let cancelled = false;
    api.getHoldings({ type: "customer", id: customer.id })
      .then((r) => { if (!cancelled) setMaterials(r.rows); })
      .catch(() => undefined);
    return () => { cancelled = true; };
  }, [opened, customer]);

  const form = useForm({
    initialValues: {
      name: customer?.name ?? "",
      contact_email: customer?.contact_email ?? "",
      contact_phone: customer?.contact_phone ?? "",
      address: customer?.address ?? "",
      city: customer?.city ?? "",
      branch: customer?.branch ?? "",
      contract_number: customer?.contract_number ?? "",
      contract_date: customer?.contract_date ?? "",
      drive_folder_id: customer?.drive_folder_id ?? "",
      notes: customer?.notes ?? "",
      is_active: customer?.is_active ?? true,
    },
    validate: {
      name: (v) => (v.trim().length === 0 ? t("customers.nameRequired") : null),
    },
  });

  useEffect(() => {
    if (opened) {
      form.setValues({
        name: customer?.name ?? "",
        contact_email: customer?.contact_email ?? "",
        contact_phone: customer?.contact_phone ?? "",
        address: customer?.address ?? "",
        city: customer?.city ?? "",
        branch: customer?.branch ?? "",
        contract_number: customer?.contract_number ?? "",
        contract_date: customer?.contract_date ?? "",
        drive_folder_id: customer?.drive_folder_id ?? "",
        notes: customer?.notes ?? "",
        is_active: customer?.is_active ?? true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, customer]);

  async function handleSubmit(values: typeof form.values) {
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.updateCustomer(customer!.id, values);
      } else {
        await api.createCustomer(values);
      }
      notifications.show({ color: "green", message: isEdit ? t("common.saved") : t("customers.createdToast") });
      onSaved();
    } catch (err) {
      const status = err instanceof ApiError ? err.status : 0;
      const msg = status === 409 ? t("customers.duplicateName") : t("common.saveFailed");
      notifications.show({ color: "red", title: t("common.error"), message: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={isEdit ? t("customers.edit", { name: customer?.name }) : t("customers.add")}
      position="right"
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput label={t("customers.nameLabel")} required {...form.getInputProps("name")} />
          <TextInput label={t("customers.emailLabel")} {...form.getInputProps("contact_email")} />
          <TextInput label={t("customers.phoneLabel")} {...form.getInputProps("contact_phone")} />
          <Textarea label={t("customers.addressLabel")} autosize minRows={2} {...form.getInputProps("address")} />
          <TextInput label={t("customers.cityLabel")} {...form.getInputProps("city")} />
          <TextInput label={t("customers.branchLabel")} {...form.getInputProps("branch")} />
          <TextInput label={t("customers.contractNumberLabel")} description={t("customers.contractNumberHint")} {...form.getInputProps("contract_number")} />
          <TextInput type="date" label={t("customers.contractDateLabel")} description={t("customers.contractDateHint")} {...form.getInputProps("contract_date")} />
          <TextInput
            label={t("customers.driveFolderLabel")}
            description={t("customers.driveFolderDesc")}
            {...form.getInputProps("drive_folder_id")}
          />
          <Textarea label={t("customers.notesLabel")} autosize minRows={2} {...form.getInputProps("notes")} />
          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={submitting}>
              {isEdit ? t("common.save") : t("common.add")}
            </Button>
          </Group>
        </Stack>
      </form>
      {isEdit && materials.length > 0 && (
        <>
          <Divider my="lg" label={t("customers.materialsTitle")} labelPosition="left" />
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t("freeIssues.colItem")}</Table.Th>
                <Table.Th ta="right">{t("freeIssues.colQty")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {materials.map((m) => (
                <Table.Tr key={m.stock_item_id}>
                  <Table.Td>{m.name ?? `#${m.stock_item_id}`}</Table.Td>
                  <Table.Td ta="right" style={{ whiteSpace: "nowrap" }}>{fmtQty(m.quantity)} {m.unit ?? ""}</Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </>
      )}
    </Drawer>
  );
}
